import React, { useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const BAND_TONE = { High: undefined, Medium: "warn", Low: "danger", Unknown: "muted" };
const SOURCE_TONE = { inherited: "muted", override: "warn", product_native: undefined };
const SOURCE_LABEL = { inherited: "Inherited", override: "Override", product_native: "Product-native" };

function renderValue(value) {
  if (value === null || value === undefined || value === "") return <em style={{ color: "var(--bo-muted)" }}>—</em>;
  if (Array.isArray(value)) {
    return <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{value.map((v, i) => <Pill key={`${v}-${i}`}>{String(v)}</Pill>)}</div>;
  }
  return <span>{String(value)}</span>;
}

function FieldCard({ field, brandId, productId, onRefresh }) {
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideValue, setOverrideValue] = useState("");
  const [overrideType, setOverrideType] = useState("replace");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submitOverride = async (e) => {
    e.preventDefault();
    setErr(null); setSaving(true);
    try {
      const value = overrideValue.includes("\n") || overrideValue.includes(",")
        ? overrideValue.split(/[\n,]/).map(s => s.trim()).filter(Boolean)
        : overrideValue.trim();
      await api.post("/intelligence/statements", {
        scope: "product",
        scope_id: productId,
        field_path: field.field_path,
        value,
        status: "APPROVED",
        authority: "APPROVED",
        lifecycle_state: "ACTIVE",
        freshness_policy: "PRODUCT_CURRENT",
        source_type: "founder_input",
        source_type_weight: 3,
        human_reviewed: true,
        parent_statement_id: field.statement_id,
        override_type: overrideType,
        override_reason: reason || null,
        change_reason: reason || "Product override",
      });
      onRefresh();
      setShowOverrideForm(false);
      setOverrideValue(""); setReason("");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bo-card" style={{ padding: 16, marginBottom: 10 }} data-testid={`pb-field-${field.field_path}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "var(--bo-mono)", fontSize: 12, color: "var(--bo-muted)" }}>{field.field_path}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Pill tone={SOURCE_TONE[field.source]} testId={`pb-source-${field.field_path}`}>{SOURCE_LABEL[field.source] || field.source}</Pill>
          <Pill tone={BAND_TONE[field.confidence_band]}>{field.confidence_band || "—"}</Pill>
          <Pill tone="muted">{field.authority || "—"}</Pill>
        </div>
      </div>
      <div style={{ marginTop: 10 }}>{renderValue(field.value)}</div>
      {field.source === "override" && field.parent_value !== undefined && field.parent_value !== null && (
        <div style={{ marginTop: 10, padding: 10, background: "var(--bo-panel-alt)", border: "1px solid var(--bo-line)", borderRadius: "var(--bo-radius-sm)", fontSize: 13 }}>
          <div style={{ color: "var(--bo-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
            Inherited value (before override):
          </div>
          {renderValue(field.parent_value)}
          {field.override_reason && <div style={{ marginTop: 6, fontStyle: "italic", color: "var(--bo-muted)" }}>Reason: {field.override_reason}</div>}
        </div>
      )}
      {field.source === "inherited" && (
        <button type="button" className="bo-btn bo-btn-ghost" style={{ padding: "4px 10px", fontSize: 12, marginTop: 8 }}
          onClick={() => setShowOverrideForm(s => !s)} data-testid={`pb-override-btn-${field.field_path}`}>
          {showOverrideForm ? "Cancel" : "Override for this product"}
        </button>
      )}
      {showOverrideForm && (
        <form onSubmit={submitOverride} style={{ marginTop: 10, padding: 10, background: "var(--bo-panel-alt)", border: "1px solid var(--bo-line)", borderRadius: "var(--bo-radius-sm)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 8 }}>
            <textarea className="bo-input" value={overrideValue} onChange={e => setOverrideValue(e.target.value)}
              placeholder="Product-specific value…" rows={2} required data-testid={`pb-override-value-${field.field_path}`} />
            <select className="bo-input" value={overrideType} onChange={e => setOverrideType(e.target.value)}>
              <option value="replace">replace</option>
              <option value="extend">extend</option>
              <option value="restrict">restrict</option>
            </select>
          </div>
          <input className="bo-input" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Reason (required for audit)" style={{ marginBottom: 8 }} required />
          {err && <div className="bo-login-error" role="alert">{err}</div>}
          <button type="submit" className="bo-btn bo-btn-primary" disabled={saving} data-testid={`pb-override-submit-${field.field_path}`}>
            {saving ? "Saving…" : "Save override"}
          </button>
        </form>
      )}
    </div>
  );
}

export default function ProductBrain() {
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (productId) => {
    if (!productId) return;
    try {
      const res = await api.get(`/intelligence/brains/product/${productId}`);
      setData(res.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/products");
        const list = r.data.products || [];
        setProducts(list);
        if (list.length > 0) {
          setSelectedId(list[0].id);
          await load(list[0].id);
        }
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    if (!data?.fields) return {};
    const out = {};
    for (const f of data.fields) {
      const d = (f.field_path || "").split(".")[0] || "other";
      if (!out[d]) out[d] = [];
      out[d].push(f);
    }
    return out;
  }, [data]);

  const selectedProduct = products.find(p => p.id === selectedId);

  return (
    <Shell title="Product Brain">
      <div className="bo-eyebrow" data-testid="pb-eyebrow">Gate 2 · Slice 3 · Product Brain</div>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }} data-testid="pb-title">
        Product Brain
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, margin: "0 0 20px 0" }} data-testid="pb-lede">
        Every product inherits Brand Brain by default. Add product-specific overrides here —
        replace, extend, or restrict any inherited field. Every override records who, when, and why.
      </p>

      {loading && <LoadingCard label="Loading products…" />}
      <ErrorBanner error={error} />

      {!loading && products.length > 0 && (
        <div className="bo-card" style={{ marginBottom: 16 }}>
          <label style={{ display: "block" }}>
            <div className="bo-eyebrow">Product</div>
            <select className="bo-input" style={{ marginTop: 6 }} value={selectedId}
              onChange={e => { setSelectedId(e.target.value); load(e.target.value); }}
              data-testid="pb-product-select">
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          {data && (
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }} data-testid="pb-counts">
              <Pill tone={undefined}>Inherited: {data.inherited_count}</Pill>
              <Pill tone="warn">Overrides: {data.override_count}</Pill>
              <Pill tone="muted">Product-native: {data.product_native_count}</Pill>
            </div>
          )}
        </div>
      )}

      {data && Object.entries(grouped).map(([domain, fields]) => (
        <section key={domain} className="bo-card" style={{ marginBottom: 16 }} data-testid={`pb-domain-${domain}`}>
          <div className="bo-eyebrow">Domain</div>
          <h2 className="bo-card-title" style={{ textTransform: "capitalize" }}>{domain.replace(/_/g, " ")}</h2>
          <div style={{ marginTop: 12 }}>
            {fields.map(f => <FieldCard key={f.field_path} field={f} brandId={data.brand_id} productId={selectedId} onRefresh={() => load(selectedId)} />)}
          </div>
        </section>
      ))}

      {data && data.fields.length === 0 && (
        <EmptyState title="No inherited fields yet" description="Add Brand Brain statements first — they'll appear here as inherited fields you can override." />
      )}
    </Shell>
  );
}
